/**
 * OneTrust tracking utility
 * Provides standalone function to track OneTrust groups updates
 * Can be called from OptanonWrapper callback which runs in global scope
 */

import { TRACKING_EVENTS, SEGMENT_COMMON_PROPERTIES } from "./constants";
import { AnalyticsBrowser } from "@segment/analytics-next";
import { addDatadogError } from "src/utils/datadog";
import { getSqreemEventProperties } from "./sqreemTracking";
import {
  getCookieValue,
  setCookieValue,
} from "src/utils/cookies/cookie-getter";

const ONETRUST_GROUPS_COOKIE_KEY = "__hh_onetrust_groups";

declare global {
  interface Window {
    __hh_segment_analytics?: AnalyticsBrowser;
    __hh_trackOneTrustGroupsUpdated?: (activeGroups?: string) => void;
    OnetrustActiveGroups?: string;
  }
}

/**
 * Get stored OneTrust groups from cookie
 * Returns null if not set (initial load), empty string if explicitly set to empty
 *
 * Legal Note: Storing consent preferences in first-party cookies is compliant with
 * GDPR/CCPA regulations. This cookie stores only the consent group identifiers
 * (not personal data) and is used solely for tracking consent changes and including
 * consent status in analytics events, which aligns with privacy regulations.
 */
export const getStoredOneTrustGroups = (): string | null => {
  if (typeof window === "undefined") return null;
  try {
    const stored = getCookieValue(ONETRUST_GROUPS_COOKIE_KEY);
    // getCookieValue returns undefined if not found, we want null to distinguish from empty string
    if (stored === undefined) return null;
    // Normalize: strip ";Path" and any trailing cookie attributes that may appear in test environment
    // In production, getCookieValue should return just the value, but this handles test environment quirks
    const normalized = stored.split(";")[0];
    return normalized;
  } catch {
    return null;
  }
};

/**
 * Store OneTrust groups in cookie
 * Uses session cookie (no expiration) to match sessionStorage behavior
 * Session cookies are automatically deleted when the browser session ends
 * Uses default domain (hingehealth.com) to ensure cookie is accessible across subdomains
 */
const storeOneTrustGroups = (activeGroups: string): void => {
  if (typeof window === "undefined") return;
  try {
    // Use session cookie (no expiration parameter) - deleted when browser closes
    // This matches sessionStorage behavior while being more reliable
    // For localhost/dev, use empty string domain (current domain only)
    // For production, use default (.hingehealth.com) for cross-subdomain access
    const isLocalhost =
      typeof window !== "undefined" &&
      (window.location.hostname === "localhost" ||
        window.location.hostname === "127.0.0.1" ||
        window.location.hostname.includes("cloudfront.net"));
    const domain = isLocalhost ? "" : undefined;

    setCookieValue(
      ONETRUST_GROUPS_COOKIE_KEY,
      activeGroups || "",
      undefined,
      domain,
    );
  } catch {
    // Silently fail - cookie storage should not break functionality
  }
};

/**
 * Track OneTrust Groups Updated event
 * This function can be called from OptanonWrapper callback
 * It uses the global Segment analytics instance
 * Only tracks when groups actually change (not on initial load)
 */
export const trackOneTrustGroupsUpdated = (activeGroups?: string) => {
  try {
    if (typeof window === "undefined") {
      return;
    }

    const groups = activeGroups || "";
    const storedGroups = getStoredOneTrustGroups();

    // If this is the initial load (no stored groups in cookie), just store and don't track
    if (storedGroups === null) {
      storeOneTrustGroups(groups);
      return;
    }

    // If groups haven't changed, don't track
    if (groups === storedGroups) {
      return;
    }

    // Groups have changed - track the update
    const analytics = window.__hh_segment_analytics;
    if (!analytics) {
      // Store groups even if analytics not ready
      storeOneTrustGroups(groups);
      return;
    }

    const hostname = window.location.hostname;
    const pathname = window.location.pathname;

    if (!hostname || !pathname) {
      return;
    }

    // Store the new groups
    storeOneTrustGroups(groups);

    const properties: Record<string, string> = {
      ...SEGMENT_COMMON_PROPERTIES,
      ...getSqreemEventProperties(),
      active_groups: groups,
      page_url: window.location.href,
      page_path: pathname,
      page_hostname: hostname,
      page_title: typeof document !== "undefined" ? document.title : "",
      referrer: typeof document !== "undefined" ? document.referrer : "",
    };

    // Track the event asynchronously with proper error handling
    analytics
      .track(TRACKING_EVENTS.ONETRUST_GROUPS_UPDATED, properties)
      .catch(trackError => {
        // Log error to Datadog for monitoring
        addDatadogError(
          "SegmentOneTrustGroupsUpdatedTrackingError",
          String(trackError),
        );
      });
  } catch (error) {
    // Silently fail - tracking should never break OneTrust functionality
    addDatadogError("SegmentOneTrustGroupsUpdatedTrackingError", String(error));
  }
};

/**
 * Initialize global OneTrust tracking function
 * Should be called after Segment analytics is initialized
 */
export const initializeOneTrustTracking = (analytics: AnalyticsBrowser) => {
  if (typeof window !== "undefined") {
    window.__hh_segment_analytics = analytics;
    window.__hh_trackOneTrustGroupsUpdated = trackOneTrustGroupsUpdated;
  }
};
